<template>
  <!-- Always inline: this bar is a segment of the toolbar row (ListingToolbar),
       between the result count and the view toggles. -->
  <div class="listing-filter-bar listing-filter-bar--inline">
    <div ref="filtersEl" class="listing-filter-bar__filters">
      <div
        v-for="filter in filterDefs"
        :key="filter.key"
        ref="itemEls"
        class="listing-filter-bar__item"
      >
        <button
          type="button"
          class="listing-filter-bar__button"
          :class="{ 'is-open': openKey === filter.key, 'is-on': isActive(filter.key) }"
          :aria-expanded="openKey === filter.key"
          :aria-controls="openKey === filter.key ? `filter-bar-menu-${filter.key}` : undefined"
          @click="toggleOpen(filter, $event)"
        >
          <span class="listing-filter-bar__label">
            <SvgIcon :name="filter.icon" :size="14" />
            <span class="listing-filter-bar__label-text">{{ buttonLabel(filter) }}</span>
          </span>
          <SvgIcon name="chevron" :size="20" class="listing-filter-bar__chevron" />
        </button>
        <div
          v-if="openKey === filter.key && filter.options"
          :id="`filter-bar-menu-${filter.key}`"
          class="listing-filter-bar__menu"
        >
          <label
            v-for="option in filter.options"
            :key="option.slug"
            class="listing-filter-bar__option"
            :class="{ 'is-selected': isSelected(filter, option.slug) }"
          >
            <input
              :type="filter.multi ? 'checkbox' : 'radio'"
              :name="filter.key"
              :checked="isSelected(filter, option.slug)"
              @change="pick(filter, option.slug)"
            />
            <span v-if="filter.multi" class="listing-filter-bar__box">
              <SvgIcon v-if="isSelected(filter, option.slug)" name="check" :size="12" />
            </span>
            <span
              v-if="option.color"
              class="listing-filter-bar__dot"
              :style="{ background: option.color }"
            ></span>
            <span class="listing-filter-bar__text">{{ option.label }}</span>
            <SvgIcon
              v-if="!filter.multi && isSelected(filter, option.slug)"
              name="check"
              :size="14"
              class="listing-filter-bar__check"
            />
          </label>
        </div>
        <!-- Invisible copy of the menu rows: gives the item (and thus the
             pill) the width of the widest option, so pill and menu align. -->
        <div v-if="filter.options" class="listing-filter-bar__sizer" aria-hidden="true">
          <span v-for="option in filter.options" :key="option.slug" class="listing-filter-bar__option">
            <span v-if="filter.multi" class="listing-filter-bar__box"></span>
            <span v-if="option.color" class="listing-filter-bar__dot"></span>
            <span class="listing-filter-bar__text">{{ option.label }}</span>
            <SvgIcon v-if="!filter.multi" name="check" :size="14" class="listing-filter-bar__check" />
          </span>
        </div>
      </div>
      <button
        v-if="hasActiveFilters"
        type="button"
        class="listing-filter-bar__clear"
        @click="clearAll"
      >
        <SvgIcon name="x" :size="14" :stroke="2" />
        {{ t('listing-filter-bar.clear') }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import SvgIcon from './SvgIcon.vue';
import {
  DEFAULT_SORT,
  SORT_OPTIONS,
  STATUS_OPTIONS,
  filterBarDefaults,
  hasActiveFilterBarCriteria
} from '../lib/criteriaContract';
import { t } from '../lib/i18n';
import { useCriteria } from '../lib/useCriteria';
import { useMapState } from '../lib/useMapState';

// State lives in the shared criteria singleton, so the search bar's "Soort
// woning" select and this bar are the same filter in two places (plan 60, B2).
const props = defineProps({
  housingTypes: { type: Array, default: () => [] }
});

const { criteria, update } = useCriteria();
const { clearBbox, requestFit } = useMapState();

const translatedOptions = (options) =>
  options.map((option) => ({ ...option, label: t(option.labelKey) }));

const filterDefs = computed(() => [
  { key: 'types', icon: 'home', options: props.housingTypes, multi: true },
  { key: 'statuses', icon: 'tag', options: translatedOptions(STATUS_OPTIONS), multi: true },
  { key: 'sort', icon: 'adjustments', options: translatedOptions(SORT_OPTIONS), multi: false }
]);

const openKey = ref(null);
const itemEls = ref([]);
const filtersEl = ref(null);

// Opening a menu releases the row's overflow clipping (see
// _listing-filter-bar.scss), which visually resets the horizontal scroll.
// Instead of preserving the old scroll offset, snap the opened pill to the
// start of the row via translateX so its menu always opens fully on screen;
// once the menu closes and the row clips again, scroll to that same spot.
let openItemEl = null;
let openTriggerEl = null;
let snappedScrollLeft = 0;

watch(openKey, (key, previousKey) => {
  const el = filtersEl.value;
  if (!el) return;
  if (key && openItemEl) {
    const paddingLeft = parseFloat(getComputedStyle(el).paddingLeft) || 0;
    const itemOffset =
      openItemEl.getBoundingClientRect().left - el.getBoundingClientRect().left - paddingLeft + el.scrollLeft;
    snappedScrollLeft = Math.max(0, Math.min(itemOffset, el.scrollWidth - el.clientWidth));
    el.style.setProperty('--scroll-shift', `-${snappedScrollLeft}px`);
  } else if (!key && previousKey) {
    el.style.removeProperty('--scroll-shift');
    nextTick(() => {
      el.scrollLeft = snappedScrollLeft;
    });
  }
});

const toggleOpen = (filter, event) => {
  if (!filter.options) return;
  const isClosing = openKey.value === filter.key;
  openItemEl = isClosing ? null : event.currentTarget.closest('.listing-filter-bar__item');
  openTriggerEl = isClosing ? null : event.currentTarget;
  openKey.value = isClosing ? null : filter.key;
};

const isSelected = (filter, slug) =>
  filter.multi ? criteria[filter.key].includes(slug) : criteria[filter.key] === slug;

const pick = (filter, slug) => {
  if (filter.multi) {
    update({
      [filter.key]: isSelected(filter, slug)
        ? criteria[filter.key].filter((item) => item !== slug)
        : [...criteria[filter.key], slug]
    });
    return;
  }
  update({ [filter.key]: slug });
  openKey.value = null;
};

const isActive = (key) => {
  if (key === 'sort') return criteria.sort !== DEFAULT_SORT;
  return criteria[key].length > 0;
};

const hasActiveFilters = computed(() => hasActiveFilterBarCriteria(criteria));

const buttonLabel = (filter) => {
  if (filter.key === 'types') {
    if (!criteria.types.length) return t('listing-search.type-select.all');
    if (criteria.types.length === 1) {
      const match = props.housingTypes.find((type) => type.slug === criteria.types[0]);
      return match ? match.label : criteria.types[0];
    }
    return t('listing-search.housing.types-count', { count: criteria.types.length });
  }
  if (filter.key === 'statuses') {
    if (!criteria.statuses.length) return t('listing-filter-bar.status.all');
    if (criteria.statuses.length === 1) {
      const match = STATUS_OPTIONS.find((option) => option.slug === criteria.statuses[0]);
      return match ? t(match.labelKey) : criteria.statuses[0];
    }
    return t('listing-filter-bar.status.count', { count: criteria.statuses.length });
  }
  // Sort is the last of the three filters this bar holds
  const match = SORT_OPTIONS.find((option) => option.slug === criteria.sort);

  return t('listing-filter-bar.sort.summary', {
    label: match ? t(match.labelKey) : criteria.sort
  });
};

// Clears only the pills this bar shows; the search bar's segments keep their
// own clear buttons (WHN-2127). Dropping the viewport bbox and asking the map to
// re-frame is part of "wissen": without it the list stays limited to whatever
// area the user had zoomed into, which reads as nothing having happened.
const clearAll = () => {
  update(filterBarDefaults());
  clearBbox();
  requestFit();
  openKey.value = null;
};

const onDocMousedown = (event) => {
  const items = itemEls.value || [];
  if (!items.some((el) => el && el.contains(event.target))) openKey.value = null;
};

const onDocKeydown = (event) => {
  if (event.key !== 'Escape' || !openKey.value) return;
  const trigger = openTriggerEl;
  openKey.value = null;
  openTriggerEl = null;
  if (trigger) trigger.focus();
};

onMounted(() => {
  document.addEventListener('mousedown', onDocMousedown, true);
  document.addEventListener('keydown', onDocKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', onDocMousedown, true);
  document.removeEventListener('keydown', onDocKeydown);
});
</script>
